import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Upload, FileText, Download, Trash2, Edit2, FolderOpen, Tag, 
  Search, Filter, X, Plus, File 
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useDialog } from '@/context/DialogContext';
import api from '@/lib/api';

export default function ProjectFilesManager({ projectId }) {
  const { confirm } = useDialog();
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileDescription, setFileDescription] = useState('');
  const [fileCategory, setFileCategory] = useState('');
  const [fileTags, setFileTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTag, setFilterTag] = useState('all');

  useEffect(() => {
    if (projectId) {
      fetchFiles();
      fetchCategories();
      fetchTags();
    }
  }, [projectId]);

  const fetchFiles = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/files`);
      setFiles(res.data || []);
    } catch (err) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/files/categories`);
      setCategories(res.data?.categories || []);
    } catch (err) {
      console.error('Failed to load categories');
    }
  };

  const fetchTags = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/files/tags`);
      setTags(res.data?.tags || []);
    } catch (err) {
      console.error('Failed to load tags');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setFileName(file.name);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    if (!fileName.trim()) {
      toast.error('File name is required');
      return;
    }

    setUploading(true);

    try {
      // First upload the file to get file_id
      const formData = new FormData();
      formData.append('file', uploadFile);

      const uploadRes = await api.post('/uploads/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fileId = uploadRes.data?.id;

      // Now add to project files
      await api.post(`/projects/${projectId}/files`, {
        name: fileName,
        description: fileDescription,
        file_id: fileId,
        category: fileCategory || 'General',
        tags: fileTags,
      });

      toast.success('File uploaded successfully');
      setUploadDialogOpen(false);
      resetUploadForm();
      fetchFiles();
      fetchCategories();
      fetchTags();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to upload file';
      toast.error(typeof msg === 'string' ? msg : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setFileName('');
    setFileDescription('');
    setFileCategory('');
    setFileTags([]);
    setTagInput('');
  };

  const handleEdit = (file) => {
    setEditingFile(file);
    setFileName(file.name);
    setFileDescription(file.description || '');
    setFileCategory(file.category || '');
    setFileTags(file.tags || []);
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!fileName.trim()) {
      toast.error('File name is required');
      return;
    }

    try {
      await api.patch(`/projects/${projectId}/files/${editingFile.id}`, {
        name: fileName,
        description: fileDescription,
        category: fileCategory || 'General',
        tags: fileTags,
      });
      toast.success('File updated');
      setEditDialogOpen(false);
      fetchFiles();
      fetchCategories();
      fetchTags();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to update file';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update file');
    }
  };

  const handleDelete = async (fileId) => {
    const ok = await confirm({
      title: "Delete file?",
      description: "This action cannot be undone.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;

    try {
      await api.delete(`/projects/${projectId}/files/${fileId}`);
      toast.success('File deleted');
      fetchFiles();
      fetchCategories();
      fetchTags();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const res = await api.get(`/uploads/file/${fileId}`, { responseType: 'blob' });
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !fileTags.includes(tag)) {
      setFileTags([...fileTags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFileTags(fileTags.filter(t => t !== tag));
  };

  // Filtered files
  const filteredFiles = files.filter(file => {
    const matchesSearch = searchQuery === '' || 
      file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      file.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || file.category === filterCategory;
    const matchesTag = filterTag === 'all' || file.tags?.includes(filterTag);

    return matchesSearch && matchesCategory && matchesTag;
  });

  // Group by category
  const filesByCategory = filteredFiles.reduce((acc, file) => {
    const cat = file.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(file);
    return acc;
  }, {});

  if (loading) return <div className="text-center py-8">Loading files...</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Project Files
          </h3>
          <p className="text-sm text-muted-foreground">{files.length} files</p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Upload File
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterTag} onValueChange={setFilterTag}>
              <SelectTrigger>
                <SelectValue placeholder="All Tags" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {tags.map(tag => (
                  <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Files List */}
      {Object.keys(filesByCategory).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <File className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No files found</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(filesByCategory).map(([category, categoryFiles]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                {category}
                <Badge variant="secondary">{categoryFiles.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {categoryFiles.map(file => (
                  <div key={file.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors">
                    <FileText className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">{file.name}</h4>
                          {file.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{file.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={file.uploaded_by_avatar} />
                                <AvatarFallback>{file.uploaded_by_name?.[0]}</AvatarFallback>
                              </Avatar>
                              <span>{file.uploaded_by_name}</span>
                            </div>
                            <span>•</span>
                            <span>{new Date(file.created_at).toLocaleDateString()}</span>
                          </div>
                          {file.tags && file.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {file.tags.map(tag => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  <Tag className="h-3 w-3 mr-1" />
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleDownload(file.file_id, file.name)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(file)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(file.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
            <DialogDescription>Add a file to this project's library</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Select File</Label>
              <Input type="file" onChange={handleFileSelect} />
              {uploadFile && (
                <p className="text-sm text-muted-foreground mt-1">
                  Selected: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            <div>
              <Label>File Name</Label>
              <Input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="Document name" />
            </div>

            <div>
              <Label>Description (Optional)</Label>
              <Textarea value={fileDescription} onChange={(e) => setFileDescription(e.target.value)} placeholder="Describe this file" rows={3} />
            </div>

            <div>
              <Label>Category</Label>
              <Input value={fileCategory} onChange={(e) => setFileCategory(e.target.value)} placeholder="e.g., Requirements, Design, Reports" list="categories" />
              {categories.length > 0 && (
                <datalist id="categories">
                  {categories.map(cat => <option key={cat} value={cat} />)}
                </datalist>
              )}
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input 
                  value={tagInput} 
                  onChange={(e) => setTagInput(e.target.value)} 
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add tag and press Enter" 
                  list="tags"
                />
                <Button type="button" onClick={addTag}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {tags.length > 0 && (
                <datalist id="tags">
                  {tags.map(tag => <option key={tag} value={tag} />)}
                </datalist>
              )}
              {fileTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {fileTags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag}
                      <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit File</DialogTitle>
            <DialogDescription>Update file metadata</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>File Name</Label>
              <Input value={fileName} onChange={(e) => setFileName(e.target.value)} />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea value={fileDescription} onChange={(e) => setFileDescription(e.target.value)} rows={3} />
            </div>

            <div>
              <Label>Category</Label>
              <Input value={fileCategory} onChange={(e) => setFileCategory(e.target.value)} list="categories" />
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input 
                  value={tagInput} 
                  onChange={(e) => setTagInput(e.target.value)} 
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add tag" 
                  list="tags"
                />
                <Button type="button" onClick={addTag}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {fileTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {fileTags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag}
                      <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
